/* eslint-disable */

/**
 * Rewritten default cut event handling to prevent removing selected node
 *
 * @see https://github.com/ProseMirror/prosemirror-view/blob/master/src/input.ts
 * @see https://github.com/ProseMirror/prosemirror-view/blob/master/src/clipboard.ts
 * @see https://github.com/ProseMirror/rfcs/pull/3
 *
 */

import { DOMSerializer, Fragment, Slice } from "@tiptap/pm/model";
import { type EditorView } from "@tiptap/pm/view";

let _detachedDoc: Document | null = null;
function detachedDoc() {
  return _detachedDoc || (_detachedDoc = document.implementation.createHTMLDocument("title"));
}

function stripCustomBlocksFromFragment(fragment: Fragment): Fragment {
  let changed = false;
  const children = [];

  for (let index = 0; index < fragment.childCount; index += 1) {
    const child = fragment.child(index);

    if (child.type.name === "customBlock") {
      changed = true;
      continue;
    }

    const childContent = stripCustomBlocksFromFragment(child.content);
    if (childContent !== child.content) {
      changed = true;
      children.push(child.copy(childContent));
    } else {
      children.push(child);
    }
  }

  return changed ? Fragment.fromArray(children) : fragment;
}

function stripCustomBlocksFromSlice(slice: Slice): Slice {
  const content = stripCustomBlocksFromFragment(slice.content);
  if (content === slice.content) {
    return slice;
  }

  return new Slice(content, slice.openStart, slice.openEnd);
}

export function serializeForClipboard(view: EditorView, slice: Slice) {
  view.someProp("transformCopied", (f) => {
    slice = f(slice!, view);
  });
  slice = stripCustomBlocksFromSlice(slice);

  let context = [],
    { content, openStart, openEnd } = slice;
  while (
    openStart > 1 &&
    openEnd > 1 &&
    content.childCount == 1 &&
    content.firstChild!.childCount == 1
  ) {
    openStart--;
    openEnd--;
    let node = content.firstChild!;
    /* @ts-ignore */
    context.push(node.type.name, node.attrs != node.type.defaultAttrs ? node.attrs : null);
    content = node.content;
  }

  let serializer =
    view.someProp("clipboardSerializer") || DOMSerializer.fromSchema(view.state.schema);
  let doc = detachedDoc(),
    wrap = doc.createElement("div");
  wrap.appendChild(serializer.serializeFragment(content, { document: doc }));

  let firstChild = wrap.firstChild,
    needsWrap,
    wrappers = 0;
  /* @ts-ignore */
  while (
    firstChild &&
    firstChild.nodeType == 1 &&
    (needsWrap = wrapMap[firstChild.nodeName.toLowerCase()])
  ) {
    for (let i = needsWrap.length - 1; i >= 0; i--) {
      let wrapper = doc.createElement(needsWrap[i]);
      while (wrap.firstChild) wrapper.appendChild(wrap.firstChild);
      wrap.appendChild(wrapper);
      wrappers++;
    }
    firstChild = wrap.firstChild;
  }

  if (firstChild && firstChild.nodeType == 1)
    (firstChild as HTMLElement).setAttribute(
      "data-pm-slice",
      `${openStart} ${openEnd}${wrappers ? ` -${wrappers}` : ""} ${JSON.stringify(context)}`,
    );

  let text =
    view.someProp("clipboardTextSerializer", (f) => f(slice, view)) ||
    slice.content.textBetween(0, slice.content.size, "\n\n");

  return { dom: wrap, text, slice };
}

const wrapMap: { [node: string]: string[] } = {
  thead: ["table"],
  tbody: ["table"],
  tfoot: ["table"],
  caption: ["table"],
  colgroup: ["table"],
  col: ["table", "colgroup"],
  tr: ["table", "tbody"],
  td: ["table", "tbody", "tr"],
  th: ["table", "tbody", "tr"],
};

/** Puts serialized content into a `copy`/`cut` event's clipboard in place of the browser's. */
export function setClipboardData(
  event: ClipboardEvent,
  { dom, text }: Pick<ReturnType<typeof serializeForClipboard>, "dom" | "text">,
) {
  const data = event.clipboardData;
  if (!data) {
    return false;
  }

  event.preventDefault();
  data.clearData();
  data.setData("text/html", dom.innerHTML);
  data.setData("text/plain", text);
  return true;
}

export function handleCut(view: EditorView, event: ClipboardEvent) {
  setClipboardData(event, serializeForClipboard(view, view.state.selection.content()));
  // This line is disabled. We want remove stuff manually:
  // if (cut) view.dispatch(view.state.tr.deleteSelection().scrollIntoView().setMeta("uiEvent", "cut"))
}
